/**
 * useSEO - Hook para gestionar meta tags dinámicos
 * Mejora el SEO actualizando title, description y Open Graph tags
 */

import { useEffect } from 'react'

interface SEOProps {
  title?: string
  description?: string
  image?: string
  url?: string
  type?: 'website' | 'article' | 'property'
  keywords?: string[]
  author?: string
  price?: string
  location?: string
}

const DEFAULT_TITLE = 'AMPI Riviera Nayarit - Portal Inmobiliario'
const DEFAULT_DESCRIPTION = 'Portal inmobiliario de la Asociación Mexicana de Profesionales Inmobiliarios Riviera Nayarit. Encuentra propiedades en venta y renta con asesores certificados.'
const DEFAULT_IMAGE = '/og-image.jpg'
const SITE_NAME = 'AMPI Riviera Nayarit'

export function useSEO({
  title,
  description,
  image,
  url,
  type = 'website',
  keywords = [],
  author,
  price,
  location
}: SEOProps = {}) {

  useEffect(() => {
    // Actualizar título
    const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE
    document.title = fullTitle

    // Helper para actualizar/crear meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name'
      let element = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement

      if (!element) {
        element = document.createElement('meta')
        element.setAttribute(attr, name)
        document.head.appendChild(element)
      }
      element.content = content
    }

    // Meta tags básicos
    setMetaTag('description', description || DEFAULT_DESCRIPTION)

    if (keywords.length > 0) {
      setMetaTag('keywords', keywords.join(', '))
    }

    if (author) {
      setMetaTag('author', author)
    }

    // Open Graph tags
    setMetaTag('og:title', fullTitle, true)
    setMetaTag('og:description', description || DEFAULT_DESCRIPTION, true)
    setMetaTag('og:image', image || DEFAULT_IMAGE, true)
    setMetaTag('og:type', type === 'property' ? 'product' : type, true)
    setMetaTag('og:site_name', SITE_NAME, true)

    if (url) {
      setMetaTag('og:url', url, true)
    }

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image')
    setMetaTag('twitter:title', fullTitle)
    setMetaTag('twitter:description', description || DEFAULT_DESCRIPTION)
    setMetaTag('twitter:image', image || DEFAULT_IMAGE)

    // Propiedades específicas para inmuebles
    if (type === 'property') {
      if (price) {
        setMetaTag('product:price:amount', price, true)
        setMetaTag('product:price:currency', 'USD', true)
      }
      if (location) {
        setMetaTag('og:locality', location, true)
      }
    }

    // Cleanup al desmontar
    return () => {
      // Restaurar título por defecto
      document.title = DEFAULT_TITLE
    }
  }, [title, description, image, url, type, keywords, author, price, location])
}

// Función helper para generar meta tags de propiedad
export function getPropertySEO(property: {
  titulo?: string
  nombre?: string
  descripcion?: string
  fotos?: string[]
  precio_actual_usd?: number
  precio?: number
  ciudad?: string
  area?: string
  tipo_propiedad?: string
  tipo_operacion?: string
}) {
  const title = property.titulo || property.nombre || 'Propiedad'
  const description = property.descripcion
    ? property.descripcion.substring(0, 160)
    : `${property.tipo_propiedad || 'Propiedad'} ${property.tipo_operacion || 'en venta'} en ${property.ciudad || property.area || 'Riviera Nayarit'}`

  const price = property.precio_actual_usd || property.precio
  const location = property.ciudad || property.area

  return {
    title,
    description,
    image: property.fotos?.[0],
    type: 'property' as const,
    price: price?.toString(),
    location,
    keywords: [
      'inmueble',
      property.tipo_propiedad,
      property.tipo_operacion,
      property.ciudad,
      property.area,
      'AMPI',
      'Riviera Nayarit'
    ].filter(Boolean) as string[]
  }
}

// Hook específico para páginas de propiedades
export function usePropertySEO(property: Parameters<typeof getPropertySEO>[0] | null) {
  const seoProps = property ? getPropertySEO(property) : {}
  useSEO(seoProps)
}

export default useSEO
